import { useRef, useCallback, useEffect } from "react";
import "./proximityText.css";

/**
 * Per-character weight/brightness based on distance from the cursor.
 * Each character's box width is locked to its heaviest (800) width so the
 * weight can animate without ever re-wrapping / shifting the layout.
 */
export default function ProximityText({ text, className = "", radius = 130 }) {
    const ref = useRef(null);
    const raf = useRef(0);

    // Lock each glyph's width to its bold-state width (prevents reflow).
    const lockWidths = useCallback(() => {
        const spans = ref.current?.querySelectorAll("[data-ch]");
        if (!spans) return;
        spans.forEach((s) => {
            s.style.transition = "none";
            s.style.width = "auto";
            s.style.fontVariationSettings = "'wght' 800";
        });
        const widths = [...spans].map((s) => s.getBoundingClientRect().width);
        spans.forEach((s, i) => {
            s.style.width = `${widths[i]}px`;
            s.style.fontVariationSettings = "'wght' 400";
        });
        requestAnimationFrame(() => spans.forEach((s) => { s.style.transition = ""; }));
    }, []);

    useEffect(() => {
        if (document.fonts?.ready) {
            document.fonts.ready.then(lockWidths);
        } else {
            lockWidths();
        }
        window.addEventListener("resize", lockWidths);
        return () => window.removeEventListener("resize", lockWidths);
    }, [lockWidths]);

    const apply = useCallback((mx, my) => {
        const spans = ref.current?.querySelectorAll("[data-ch]");
        if (!spans) return;
        spans.forEach((s) => {
            const r = s.getBoundingClientRect();
            const cx = r.left + r.width / 2;
            const cy = r.top + r.height / 2;
            const d = Math.hypot(mx - cx, my - cy);
            const t = Math.max(0, 1 - d / radius);
            const eased = t * t;
            const weight = Math.round(300 + eased * 500);
            const light = 0.55 + eased * 0.45;
            s.style.fontVariationSettings = `'wght' ${weight}`;
            s.style.opacity = light;
        });
    }, [radius]);

    const onMove = useCallback((e) => {
        const mx = e.clientX, my = e.clientY;
        cancelAnimationFrame(raf.current);
        raf.current = requestAnimationFrame(() => apply(mx, my));
    }, [apply]);

    const onLeave = useCallback(() => {
        const spans = ref.current?.querySelectorAll("[data-ch]");
        spans?.forEach((s) => {
            s.style.fontVariationSettings = `'wght' 400`;
            s.style.opacity = 1;
        });
    }, []);

    const words = String(text).split(" ");

    return (
        <p
            ref={ref}
            className={`proxText ${className}`}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            aria-label={text}
        >
            {words.map((word, wi) => (
                <span className="proxWord" key={wi} aria-hidden="true">
                    {[...word].map((ch, ci) => (
                        <span className="proxCh" data-ch key={ci}>{ch}</span>
                    ))}
                    {wi < words.length - 1 ? <span className="proxCh" data-ch>{" "}</span> : null}
                </span>
            ))}
        </p>
    );
}
